/**
 * Polymorphic user notification system.
 *
 * Describes HOW a message should be presented to the user.
 * Each "channel" is a discriminated-union member keyed by `via`.
 *
 * Extend the system by adding a new member to {@link UserMessage}
 * and a corresponding handler in `scripts/utils/notify`.
 *
 * @example
 *   // Toast — info
 *   showMessage({ via: MESSAGE_CHANNELS.TOAST, payload: { severity: "info", text: "Saved!" } })
 *
 *   // Toast — error from API
 *   showMessage({ via: MESSAGE_CHANNELS.TOAST, payload: { severity: "error", text: apiError.message } })
 *
 *   // Modal — single confirm button
 *   showMessage({
 *     via: MESSAGE_CHANNELS.MODAL,
 *     payload: {
 *       title: "Heads up",
 *       content: "Something went wrong.",
 *       buttons: [{ label: "OK" }],
 *     },
 *   })
 *
 *   // Modal — confirm + cancel
 *   showMessage({
 *     via: MESSAGE_CHANNELS.MODAL,
 *     payload: {
 *       title: "Delete item?",
 *       content: "This cannot be undone.",
 *       buttons: [{ label: "Delete", variant: "danger", onClick: doDelete }, { label: "Cancel" }],
 *     },
 *   })
 *
 *   // Form — maps server errors into react-hook-form fields
 *   showMessage({ via: MESSAGE_CHANNELS.FORM, payload: { error: apiError, setError } })
 *
 *   // Inline — lightweight, component owns the rendering
 *   const msg = createInlineMessage(apiError)
 */

import type { FieldValues, UseFormSetError } from "react-hook-form"
import type { ApiError } from "./api"
import type { ModalContent } from "scripts/components/react/Modal.types"

export const MESSAGE_CHANNELS = {
  TOAST: "toast",
  MODAL: "modal",
  FORM: "form",
  INLINE: "inline",
} as const

type Values<T extends Record<string, unknown>> = T[keyof T]

type Message<
  Via extends Values<typeof MESSAGE_CHANNELS>,
  Payload extends Record<string, unknown>,
> = {
  via: Via
  payload: Payload
}

/** Visual severity level. Affects icon / colour of a toast. */
export type MessageSeverity = "info" | "error"

export type ToastMessage = Message<
  typeof MESSAGE_CHANNELS.TOAST,
  {
    /** Visual severity — drives icon & colour. */
    severity: MessageSeverity
    /** Human-readable text shown inside the toast. */
    text: string
    /** Duration in ms. Defaults to 3 000; pass -1 for persistent. */
    duration?: number
  }
>

export type ModalMessage = Message<typeof MESSAGE_CHANNELS.MODAL, ModalContent>

export type FormMessage = Message<
  typeof MESSAGE_CHANNELS.FORM,
  {
    /** The API error to process. */
    error: ApiError
    /** react-hook-form's setError — receives field-level or root errors. */
    setError: UseFormSetError<FieldValues>
  }
>

export type InlineMessage = Message<
  typeof MESSAGE_CHANNELS.INLINE,
  {
    /** The original API error — the component decides how to render it. */
    error: ApiError
  }
>

/**
 * Union of all supported channels.
 *
 * To add a new channel:
 * 1. Add a constant to {@link MESSAGE_CHANNELS}.
 * 2. Define a `Message<CHANNEL, Payload>` type and export it.
 * 3. Add it here.
 * 4. Add a handler in `scripts/utils/notify` — the `assertNever` guard
 *    will cause a compile error until you do.
 */
export type UserMessage = ToastMessage | ModalMessage | FormMessage | InlineMessage

type Via<T extends UserMessage> = T["via"]

type PickFromUnion<
  UnionType extends UserMessage,
  Discriminant extends Via<UnionType>,
> = UnionType extends { via: Discriminant } ? UnionType : never

export const createTypeGuard =
  <T extends Via<UserMessage>>(via: T) =>
  (message: UserMessage): message is PickFromUnion<UserMessage, T> =>
    message.via === via
